/**
 * Layout auto-completion dropdown for @ symbol layout input
 * Shows layout suggestions from state.json as user types @layout_name
 */

import { useState, useEffect, useRef, forwardRef, useImperativeHandle } from 'react'
import ReactDOM from 'react-dom'
import { Layout, Tag } from 'lucide-react'
import { Card } from '../ui/card'
import { Button } from '../ui/button'
import layoutMentionService, { LayoutMentionItem } from '../../services/layoutMentionService'

interface LayoutAutoCompleteProps {
  inputValue: string
  conversationId?: string
  onLayoutSelect: (layout: LayoutMentionItem) => void
  onClose: () => void
  visible: boolean
  position: { top: number; left: number; showAbove?: boolean }
}

export interface LayoutAutoCompleteRef {
  selectNext: () => void
  selectPrevious: () => void
  selectCurrent: () => void
  isVisible: () => boolean
}

export const LayoutAutoComplete = forwardRef<LayoutAutoCompleteRef, LayoutAutoCompleteProps>(
  ({ inputValue, conversationId, onLayoutSelect, onClose, visible, position }, ref) => {
    const [suggestions, setSuggestions] = useState<LayoutMentionItem[]>([])
    const [loading, setLoading] = useState(false)
    const [selectedIndex, setSelectedIndex] = useState(0)
    const [hasMore, setHasMore] = useState(false)
    const [searchTerm, setSearchTerm] = useState('')
    const dropdownRef = useRef<HTMLDivElement>(null)

    // Expose methods to parent component
    useImperativeHandle(ref, () => ({
      selectNext: () => {
        setSelectedIndex(prev => 
          prev < suggestions.length - 1 ? prev + 1 : 0
        )
      },
      selectPrevious: () => {
        setSelectedIndex(prev => 
          prev > 0 ? prev - 1 : suggestions.length - 1
        )
      },
      selectCurrent: () => {
        if (suggestions[selectedIndex]) {
          handleLayoutSelect(suggestions[selectedIndex])
        }
      },
      isVisible: () => visible && suggestions.length > 0
    }))

    // Load suggestions when input changes
    useEffect(() => {
      if (!visible) {
        setSuggestions([])
        return
      }

      const loadSuggestions = async () => {
        setLoading(true)
        try {
          const result = await layoutMentionService.getAutoCompleteSuggestions(
            inputValue,
            { conversationId, maxResults: 10 }
          )
          
          setSuggestions(result.items)
          setHasMore(result.hasMore)
          setSearchTerm(result.searchTerm)
          setSelectedIndex(0) // Reset selection when suggestions change
        } catch (error) {
          console.error('Error loading layout suggestions:', error)
          setSuggestions([])
        } finally {
          setLoading(false)
        }
      }

      // Debounce the search
      const timeoutId = setTimeout(loadSuggestions, 150)
      return () => clearTimeout(timeoutId)
    }, [inputValue, conversationId, visible])

    // Handle clicks outside dropdown
    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
          onClose()
        }
      }

      if (visible) {
        document.addEventListener('mousedown', handleClickOutside)
        return () => document.removeEventListener('mousedown', handleClickOutside)
      }
    }, [visible, onClose])

    const handleLayoutSelect = (layout: LayoutMentionItem) => {
      onLayoutSelect(layout)
      onClose()
    }


    // Don't render if not visible
    if (!visible) {
      return null
    }
    
    // Don't render if no suggestions and not loading
    if (suggestions.length === 0 && !loading) {
      return null
    }

    return ReactDOM.createPortal(
      <div
        ref={dropdownRef}
        className="fixed"
        style={{
          top: position.top,
          left: position.left,
          width: '320px',
          zIndex: 9999,
        }}
      >
        <Card className="shadow-2xl border bg-background max-h-64 overflow-hidden">
          {/* Header */}
          <div className="px-3 py-2 border-b border-border/30 bg-muted/30">
            <div className="flex items-center gap-2">
              <Layout className="h-3.5 w-3.5 text-muted-foreground" />
              <span className="text-xs font-medium text-muted-foreground">
                Layout Mentions
                {searchTerm && (
                  <span className="ml-1 text-primary">
                    "{searchTerm}"
                  </span>
                )}
              </span>
              {hasMore && (
                <span className="text-xs text-muted-foreground/70 ml-auto">
                  +more
                </span>
              )}
            </div>
          </div>

          {/* Loading State */}
          {loading && (
            <div className="px-3 py-4 text-center">
              <div className="text-xs text-muted-foreground">
                Loading layouts...
              </div>
            </div>
          )}
          
          {/* No suggestions but still showing dropdown */}
          {!loading && suggestions.length === 0 && (
            <div className="px-3 py-4 text-center">
              <div className="text-xs text-muted-foreground">
                No layouts found
              </div>
            </div>
          )}

          {/* Suggestions List */}
          {!loading && suggestions.length > 0 && (
            <div className="max-h-48 overflow-y-auto">
              {suggestions.map((layout, index) => (
                <Button
                  key={layout.id}
                  variant="ghost"
                  className={`w-full justify-start px-3 py-2 h-auto rounded-none transition-colors ${
                    index === selectedIndex 
                      ? 'bg-primary/10 border-l-2 border-l-primary' 
                      : 'hover:bg-slate-200 dark:hover:bg-slate-700'
                  }`}
                  onClick={() => handleLayoutSelect(layout)}
                >
                  <div className="flex items-start gap-3 w-full text-left">
                    {/* Layout Icon */}
                    <div className="flex-shrink-0 mt-0.5">
                      <Layout className={`h-3.5 w-3.5 ${
                        index === selectedIndex ? 'text-primary' : 'text-muted-foreground'
                      }`} />
                    </div>

                    {/* Layout Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className={`text-sm font-medium truncate ${
                          index === selectedIndex ? 'text-primary' : 'text-foreground'
                        }`}>
                          {layout.title}
                        </span>
                      </div>
                      
                      <div className="flex items-center gap-1 mt-1">
                        <span className="text-xs text-muted-foreground font-mono">
                          @{layout.id}
                        </span>
                        
                        {/* Tags */}
                        {layout.tags && layout.tags.length > 0 && (
                          <div className="flex items-center gap-1 ml-2">
                            <Tag className="h-2.5 w-2.5 text-muted-foreground/60" />
                            <span className="text-xs text-muted-foreground/80">
                              {layout.tags.slice(0, 2).join(', ')}
                              {layout.tags.length > 2 && '...'}
                            </span>
                          </div>
                        )}
                      </div>
                      
                      {/* Description */}
                      {layout.description && (
                        <div className="text-xs text-muted-foreground/70 mt-1 line-clamp-1">
                          {layout.description}
                        </div>
                      )}
                    </div>
                  </div>
                </Button>
              ))}
            </div>
          )}


          {/* Footer */}
          <div className="px-3 py-2 border-t border-border/30 bg-muted/20">
            <div className="text-xs text-muted-foreground/70 text-center">
              ↑↓ navigate • Enter select • Esc close
            </div>
          </div>
        </Card>
      </div>,
      document.body
    )
  }
)

LayoutAutoComplete.displayName = 'LayoutAutoComplete'